import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  type LucideIcon,
} from "lucide-react";

export type ToastVariant = "success" | "warning" | "error" | "info";

export interface ToastAction {
  label: string;
  onPress: () => void;
}

export interface ToastOptions {
  durationMs?: number;
  action?: ToastAction;
}

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  options?: ToastOptions;
  /** GA-DS-02: true while the exit animation is playing — item stays in DOM
   *  until the CSS transition completes (~300ms), then is removed. */
  exiting?: boolean;
}

interface ToastApi {
  success: (msg: string, opts?: ToastOptions) => void;
  warning: (msg: string, opts?: ToastOptions) => void;
  error: (msg: string, opts?: ToastOptions) => void;
  info: (msg: string, opts?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 3000,
  info: 3500,
  warning: 5000,
  error: 6000,
};

const MAX_STACK = 3;

const VARIANT_STYLE: Record<
  ToastVariant,
  {
    bg: string;
    border: string;
    iconColor: string;
    icon: LucideIcon;
    textColor: string;
  }
> = {
  success: {
    bg: "bg-success-soft",
    border: "border-success-soft",
    iconColor: "text-success",
    icon: CheckCircle2,
    textColor: "text-success-fg",
  },
  warning: {
    bg: "bg-warn-soft",
    border: "border-warn-soft",
    iconColor: "text-warn",
    icon: AlertTriangle,
    textColor: "text-warn-fg",
  },
  error: {
    bg: "bg-danger-soft",
    border: "border-danger-soft",
    iconColor: "text-danger",
    icon: AlertCircle,
    textColor: "text-danger-fg",
  },
  info: {
    bg: "bg-primary-soft",
    border: "border-primary-soft",
    iconColor: "text-primary",
    icon: Info,
    textColor: "text-primary-strong",
  },
};

let counter = 0;

/**
 * Heuristic — detect backend auth-error strings that should NEVER show
 * as a raw toast (they confuse non-tech users with technical phrasing
 * like "Missing or malformed Authorization header"). Matches against
 * the rendered toast message AFTER i18n interpolation, so any caller
 * that does `t("foo.fail", { error: err.message })` with a 401-flavor
 * backend error gets caught here.
 *
 * Caught messages are silently re-routed to the global
 * AuthExpiredModal (apps/isale-web/src/shared/components/
 * AuthExpiredModal.tsx) by firing the same `isale:auth-expired` event
 * the api-client fires on refresh-token failure. The modal then handles
 * the 5-second countdown + redirect UX uniformly across the app.
 *
 * This is a defense-in-depth layer on top of the api-client's
 * tryRefresh path (which already dispatches the event when refresh
 * fails). It covers two gaps:
 *   1. `apiRequest` raises ApiError(401) and a caller catches it +
 *      renders `err.message` into a toast string — the string slips
 *      past the modal because the modal only fires on refresh-fail.
 *   2. Backend rejects the request with a 401-flavor message at a
 *      non-401 status (e.g. the consent middleware or a route guard
 *      that returns 403 with "Authorization required" body).
 *
 * Added 2026-05-23 PM (Vincent feedback: "việc xử lý token expire nên
 * để ở scope globally thay vì đang đặt từng màn hình"). Pattern list is
 * conservative — only matches strings that contain BOTH the action
 * word and a clear auth indicator, so legitimate user-facing errors
 * about other things (e.g. "Authorization is being verified") aren't
 * accidentally suppressed.
 */
const AUTH_ERROR_PATTERNS = [
  /Missing or malformed Authorization/i,
  /Authorization header/i,
  /401\b.*\b(unauthor|expired|invalid token)/i,
  /\b(token expired|invalid token|session expired)\b/i,
  /\bUnauthorized\b/i,
] as const;

function looksLikeAuthError(message: string): boolean {
  return AUTH_ERROR_PATTERNS.some((rx) => rx.test(message));
}

/**
 * Web toast — top-right stack. Same imperative API as mobile.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  /**
   * GA-DS-02: Two-phase dismiss.
   *   Phase 1 — mark item as `exiting: true` so ToastView applies the
   *             exit CSS class (slide-right + fade-out, 300ms transition).
   *   Phase 2 — after transition completes, remove from state.
   *
   * A WeakMap isn't usable here (ids are primitives) so we guard against
   * double-dismiss with a simple closure check.
   */
  const dismiss = useCallback((id: number) => {
    setItems((arr) => {
      const item = arr.find((t) => t.id === id);
      if (!item || item.exiting) return arr; // already exiting or gone
      return arr.map((t) => (t.id === id ? { ...t, exiting: true } : t));
    });
    // Allow the 300ms CSS transition to finish, then remove.
    setTimeout(() => {
      setItems((arr) => arr.filter((t) => t.id !== id));
    }, 320);
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions) => {
      const id = ++counter;
      setItems((arr) => {
        const next = [...arr, { id, variant, message, options }];
        return next.slice(-MAX_STACK);
      });
      const duration = options?.durationMs ?? DEFAULT_DURATION[variant];
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  // Wrap error toast — if the rendered message smells like an auth
  // failure, suppress the toast and fire the global auth-expired event
  // instead. The AuthExpiredModal owns the redirect UX from there.
  // See `looksLikeAuthError` doc above for the full reasoning.
  const errorWithAuthGuard = useCallback(
    (m: string, o?: ToastOptions) => {
      if (looksLikeAuthError(m)) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("isale:auth-expired"));
        }
        // Drop the toast — modal will handle the UX. Console keeps
        // the original string for dev visibility.
        // eslint-disable-next-line no-console
        console.warn("[toast] suppressed auth-error toast:", m);
        return;
      }
      push("error", m, o);
    },
    [push],
  );

  const api: ToastApi = {
    success: (m, o) => push("success", m, o),
    warning: (m, o) => push("warning", m, o),
    error: errorWithAuthGuard,
    info: (m, o) => push("info", m, o),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
        role="region"
      >
        {items.map((t) => (
          <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToast() must be used inside <ToastProvider />. Mount the provider in main.tsx.",
    );
  }
  return ctx;
}

function ToastView({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: () => void;
}) {
  const style = VARIANT_STYLE[item.variant];
  const Icon = style.icon;
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 10);
    return () => clearTimeout(t);
  }, []);

  // GA-DS-02: exit state mirrors the initial (pre-enter) CSS position so
  // the toast slides right + fades out on dismiss. `motion-reduce:` classes
  // skip the translation for users who prefer reduced motion.
  const visibilityClass = item.exiting || !entered
    ? "translate-x-4 opacity-0 motion-reduce:translate-x-0"
    : "translate-x-0 opacity-100";

  return (
    <div
      role="alert"
      className={`pointer-events-auto rounded-2xl border p-3 shadow-lg transition-all duration-300 ${
        style.bg
      } ${style.border} ${visibilityClass}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 ${style.iconColor}`} size={18} strokeWidth={2} />
        <div className="flex-1">
          <p className={`text-cap font-bold ${style.textColor}`}>{item.message}</p>
          {item.options?.action && (
            <button
              type="button"
              className={`mt-1 text-xs font-bold ${style.iconColor} hover:underline`}
              onClick={() => {
                item.options?.action?.onPress();
                onDismiss();
              }}
            >
              {item.options.action.label} →
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Đóng"
          className={`flex h-6 w-6 items-center justify-center rounded-full hover:bg-black/5 ${style.iconColor}`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
